import React from 'react';
import { useNow } from '../hooks/useNow';
import { formatCountdown } from '../utils/format';
import { formatDayLong } from '../utils/lifeGrid';

/** Waking hours, not the calendar day — the night should not fill the bar. */
const WAKE_HOUR = 7;
const SLEEP_HOUR = 23;
const HOURS = SLEEP_HOUR - WAKE_HOUR;

/**
 * The "today" strip: waking hours as ticks crossed out up to the current hour,
 * background filled to the share of the day already spent, and seconds you can
 * actually watch running out. In the big grid that motion is invisible — here a
 * single day is stretched across the full width.
 */
export function TodayStrip() {
  const now = useNow(1000);
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), WAKE_HOUR);
  const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), SLEEP_HOUR);
  const before = now.getTime() < dayStart.getTime();
  const after = now.getTime() >= dayEnd.getTime();
  const progress = before ? 0 : after ? 1 : (now.getTime() - dayStart.getTime()) / (dayEnd.getTime() - dayStart.getTime());
  return <section className="border-line bg-paper relative shrink-0 overflow-hidden border-t" aria-label="Today" data-id="element-109" data-mp="11lyw6kbnq9bx">
      <div aria-hidden className="bg-accent-soft absolute inset-y-0 left-0" style={{
      width: `${progress * 100}%`
    }} data-id="element-110" data-mp="wmncb2fzunq4" />
      {!before && !after && <div aria-hidden className="bg-accent absolute inset-y-0" style={{
      left: `${progress * 100}%`,
      width: 2
    }} data-id="element-111" data-mp="1pbt5irwrpfrf" />}

      <div className="relative flex items-center gap-4 px-4 py-2.5 sm:gap-6 sm:px-6" data-id="element-112" data-mp="1kdrkw2jp18du">
        <p className="text-muted shrink-0 font-mono text-[11px]" data-id="element-113" data-mp="1fkq6dvnefaus">
          today<span className="mx-1.5" data-id="element-114" data-mp="1bjmqj95cpetk">·</span>
          <span className="text-ink" data-id="element-115" data-mp="1bild4y0v4g8o">{formatDayLong(now)}</span>
        </p>

        <div className="relative hidden h-4 min-w-0 flex-1 items-center sm:flex" aria-hidden data-id="element-116" data-mp="28456ba7rw8zg">
          <div className="grid w-full items-center" style={{
          gridTemplateColumns: `repeat(${HOURS}, minmax(0, 1fr))`
        }} data-id="element-117" data-mp="23b2cd0z32xiq">
            {Array.from({
            length: HOURS
          }, (_, index) => <span key={index} className="flex items-center justify-center" data-id="element-118" data-mp="6cmtk7ni2ymo">
                <span className={now.getHours() > WAKE_HOUR + index ? 'bg-ink' : 'bg-faint'} style={{
              width: 1,
              height: 7
            }} data-id="element-119" data-mp="3nt4sdte9cwmf" />
              </span>)}
          </div>
          <span className="bg-ink absolute left-0 top-1/2 -translate-y-1/2" style={{
          width: `${progress * 100}%`,
          height: 1.5
        }} data-id="element-120" data-mp="3evaz6rbsij3h" />
        </div>

        <p className="text-muted ml-auto shrink-0 font-mono text-[11px] sm:ml-0" data-id="element-121" data-mp="34wlfct0c2okl">
          {before ? <>
              <span className="text-accent tabular-nums" data-id="element-122" data-mp="39vybk4d3h7zu">{formatCountdown(dayStart, now)}</span>
              <span className="ml-1.5" data-id="element-123" data-mp="131kpht6zl2lf">until the day starts</span>
            </> : after ? <span data-id="element-124" data-mp="180e79xtb3fre">the day is over</span> : <>
              <span className="text-ink tabular-nums" data-id="element-125" data-mp="2slc77anhu30j">{Math.round(progress * 100)}%</span>
              <span className="mx-1.5" data-id="element-126" data-mp="2nn0wyytadqhs">·</span>
              <span className="text-accent tabular-nums" data-id="element-127" data-mp="32am2t0hjq3te">{formatCountdown(dayEnd, now)}</span>
              <span className="ml-1.5" data-id="element-128" data-mp="2xjvx1m57jyy6">left today</span>
            </>}
        </p>
      </div>
    </section>;
}